import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useState } from 'react';
import styles from './clinicalBackground.module.css';
import ClinicalBackgroundCommentModal from "../modals/clinicalBackgroundCommentModal/clinicalBackgroundCommentModal";
import ClinicalBackgroundMetaData from "../clinicalBackgroundMetaData/clinicalBackgroundMetaData";
import { TClinicalSummaryLatest, TClinicalSummaryHistoryItem } from "../../services/types/ai.types";
import { parseSummaryText } from "../../utils/utils";
import clockIcon from "../../images/clock_icon.svg";
import TranslatableText from "../translatableText/translatableText";
import { useTranslatableText } from "../translatableText/useTranslatableText";
import TranslateButton from "../translatableText/translateButton";

const ClinicalBackground = ({
  title,
  description,
  icon,
  isAiSummary,
  userGuid,
  showHistoryButton,
  latestSummary,
  historySummaries,
  translatable = true,
}: {
  title: string;
  description: string;
  icon: string;
  isAiSummary?: boolean;
  userGuid?: string;
  showHistoryButton?: boolean;
  latestSummary?: TClinicalSummaryLatest;
  historySummaries?: TClinicalSummaryHistoryItem[];
  translatable?: boolean;
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState(false);
  
  const toggleHistory = () => {
    setShowHistory(prev => !prev);
  };

  // Sort history summaries by id descending (latest first)
  const sortedHistorySummaries = React.useMemo(() => {
    if (!historySummaries || historySummaries.length === 0) return [];
    return [...historySummaries].sort((a, b) => {
      return b.id - a.id; // Descending order (latest first)
    });
  }, [historySummaries]);

  const mainSummaryTranslation = useTranslatableText(
    description,
    translatable && !!description.trim()
  );
// console.log(historySummaries, sortedHistorySummaries);
  return (
    <>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.iconContainer}></div>
          {/* <img src={icon} className={styles.icon} alt={t('ICON')} /> */}
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.tags}>
            <span className={styles.tag}>{t('SUMMARY_BY_AI')}</span>
          </div>
        </div>
        {latestSummary && (latestSummary.updatedDate || latestSummary.createdAt) && (
          <ClinicalBackgroundMetaData
            version={latestSummary.version}
            updateReason={latestSummary.updateReason}
            questionnaireId={latestSummary.questionnaireId}
            date={latestSummary.updatedDate || latestSummary.createdAt}
          />
        )}
        
        {/* Latest Version - Always Visible */}
        <div className={styles.description}>
          {mainSummaryTranslation.hasContent && (
            <p className={styles.descriptionText}>
              {mainSummaryTranslation.displayText}
            </p>
          )}
          <div className={styles.descInnerContainer}>
            <button
              type="button"
              className={styles.button}
              onClick={() => setModalOpen(true)}
            >
              {t('ADD_COMMENT')}
            </button>
            <div className={styles.actionButtons}>
              {showHistoryButton && sortedHistorySummaries && sortedHistorySummaries.length > 0 && (
                <button
                  type="button"
                  className={`${styles.button} ${styles.button_type_history}`}
                  onClick={toggleHistory}
                >
                  <img src={clockIcon} alt="" className={styles.buttonIcon} />
                  {showHistory ? t('HISTORY_VIEW_CURRENT') : t('HISTORY_VIEW_HISTORY')}
                </button>
              )}
              {mainSummaryTranslation.showTranslateButton && (
                <TranslateButton
                  label={mainSummaryTranslation.buttonLabel}
                  isTranslated={mainSummaryTranslation.isTranslated}
                  isLoading={mainSummaryTranslation.isLoading}
                  onClick={mainSummaryTranslation.handleTranslate}
                  ariaLabel={
                    mainSummaryTranslation.isTranslated
                      ? t("SHOW_ORIGINAL")
                      : t("TRANSLATE_TO_MY_LANGUAGE")
                  }
                  variant="action"
                />
              )}
            </div>
          </div>
        </div>

        {/* Therapist Response - Separate History Container */}
        {showHistory && latestSummary && latestSummary.therapistResponse && (
                  <div className={`${styles.description} ${styles.description_type_green}`}>
            <div className={styles.description}>
              <ClinicalBackgroundMetaData
                questionnaireId={latestSummary.questionnaireId}
                date={latestSummary.updatedDate}
                userName={latestSummary.updatedByUserName}
              />
              
              <div className={styles.historyInsightsSection}>
                <h4 className={styles.historyInsightsTitle}>{t("THERAPIST_RESPONSE")}</h4>
                <TranslatableText
                  text={latestSummary.therapistResponse}
                  className={styles.descriptionText}
                />
              </div>
            </div>
          </div>
        )}

        {/* History Versions - Show when expanded */}
        {showHistory && sortedHistorySummaries && sortedHistorySummaries.length > 0 && (
          <div className={styles.historyContainer}>
            {sortedHistorySummaries.map((item: TClinicalSummaryHistoryItem) => (
              <React.Fragment key={item.id}>
                <div className={styles.description}>
                <ClinicalBackgroundMetaData
                  version={item.version}
                  updateReason={item.updateReason}
                  questionnaireId={item.questionnaireId}
                  date={item.createdAt}
                />
                
                <TranslatableText
                  text={parseSummaryText(item.summary) || t("NO_INFORMATION")}
                  className={styles.descriptionText}
                  translatable={!!parseSummaryText(item.summary)?.trim()}
                />
                </div>
                {item.therapistResponse && (
                  <div className={`${styles.description} ${styles.description_type_green}`}>
                    <ClinicalBackgroundMetaData
                      questionnaireId={item.questionnaireId}
                      date={item.updatedDate || item.createdAt}
                      userName={item.updatedByUserName}             
                    />
                    <div className={styles.historyInsightsSection}>
                      <h4 className={styles.historyInsightsTitle}>{t("THERAPIST_RESPONSE")}</h4>
                      <TranslatableText
                        text={item.therapistResponse}
                        className={styles.descriptionText}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {userGuid && (
        <ClinicalBackgroundCommentModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          userGuid={userGuid}
        />
      )}
    </>
  );
};

export default ClinicalBackground;